/**
 * queue-cells — small shared cell renderers for the segmented queue table:
 * element name, layer (icon + label), removal reason, tracker id. The
 * tri-state tracker badge lives in @/components/ui (TrackerStateBadge).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, type ReactNode } from 'react';
import { ArrowUpCircle } from 'lucide-react';
import type {
  FlowLayer,
  QueueElement,
  QueueElementRule,
} from '@trafficpulse/shared';

import { Badge } from '@/components/ui';
import { cn, truncate } from '@/lib/utils';

import { layerIcon } from '../queue-icons';
import { layerLabel } from '../queue-status.util';

/** Truncated element name with the full name on hover.
 *  `min-w-0` lets it shrink inside a flex row instead of shoving a chip beside
 *  it onto a second line — one wrapped name doubles that row's height. */
export const RowName: FC<{ name: string }> = ({ name }) => (
  <span className="block min-w-0 max-w-[18rem] truncate" title={name}>
    {name}
  </span>
);

/** Element name + a "Next up" chip when this is the next-to-promote for its layer. */
export const NameWithNextCell: FC<{
  name: string;
  next: boolean;
  /** Name of the live element this queued one replaces on the next run. */
  replaces?: string;
  /** Optional status chip rendered beside the name — usually nothing. */
  badge?: ReactNode;
}> = ({
  name,
  next,
  replaces,
  badge,
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5',
      // A chip stays on the name's line — the name truncates to make room
      // rather than the row growing to fit both.
      badge ? 'flex-nowrap' : 'flex-wrap',
    )}
  >
    <RowName name={name} />
    {badge}
    {next ? (
      <span
        className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary motion-safe:animate-next-up"
        title="Top of this layer's queue — the highest-priority candidate to promote when a matching slot frees up"
      >
        <ArrowUpCircle className="h-3 w-3" aria-hidden />
        Next up
      </span>
    ) : null}
    {replaces ? (
      <span className="text-[10px] text-muted-foreground">
        replaces <span className="font-medium text-foreground">{replaces}</span>
      </span>
    ) : null}
  </span>
);

/** Routing rule pill: the Default bucket (secondary) or a named rule (success). */
export const RuleBadge: FC<{ rule?: QueueElementRule }> = ({ rule }) => {
  if (!rule) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge tone={rule.isDefault ? 'secondary' : 'success'} className="max-w-[12rem] truncate">
      <span className="truncate" title={rule.isDefault ? 'Default paths' : rule.name}>
        {rule.isDefault ? 'Default' : rule.name}
      </span>
    </Badge>
  );
};

/** Layer — a distinct lucide glyph next to the layer label. */
export const LayerCell: FC<{ layer: FlowLayer }> = ({ layer }) => {
  const Icon = layerIcon(layer);
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      {layerLabel(layer)}
    </span>
  );
};

/** Removal reason plus a small pill for the rule that removed it. */
export const RemovalReasonCell: FC<{ element: QueueElement }> = ({ element }) => (
  <span className="inline-flex items-center gap-1">
    <span
      className="block max-w-[16rem] truncate"
      title={element.removedReason ?? ''}
    >
      {element.removedReason || '—'}
    </span>
    {element.removedByRuleId ? (
      <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[9px] font-medium text-muted-foreground">
        {element.removedByRuleId}
      </span>
    ) : null}
  </span>
);

/** Tracker id, monospace + truncated (full value on hover). */
export const TrackerIdCell: FC<{ itemId: string }> = ({ itemId }) => (
  // nowrap is load-bearing: a uuid breaks at the hyphens given half a chance,
  // and one wrapped id sets the height of every row in the table.
  <span
    className="whitespace-nowrap font-mono text-[11px] text-muted-foreground"
    title={itemId}
  >
    {truncate(itemId, 12)}
  </span>
);

/**
 * The row's controls, under the Actions header: the timeline first, then a
 * hairline, then the verbs. The timeline used to sit in a headerless column of
 * its own, which parked it mid-table with nothing to belong to — it is a row
 * control like the rest, it just LOOKS at the row instead of changing it, and
 * the rule reads left-to-right: inspect, then act.
 */
export const RowActionsCell: FC<{
  timeline: ReactNode;
  children: ReactNode;
}> = ({ timeline, children }) => (
  <span className="inline-flex items-center gap-1.5">
    {timeline}
    <span className="h-4 w-px shrink-0 bg-border/70" aria-hidden />
    {children}
  </span>
);
